import { endpoints } from "config";
import { InvoiceListData, EndpointsPermissions } from "./InvoicesList.types";
import { CellOptions } from "components/newFlexTable/Cell";

export const INVOICES_LIST_COLUMNS = {
  Id: "invoices__Id" as const,
  FileName: "invoices__FileName" as const,
  SupplierName: "invoices__SupplierName" as const,
  InvoiceNumber: "invoices__InvoiceNumber" as const,
  InvoiceAmount: "invoices__InvoiceAmount" as const,
  InvoiceDate: "invoices__InvoiceDate" as const,
  InvoiceReceivedDate: "invoices__InvoiceReceivedDate" as const,
};

export const INVOICES_LIST_ENDPOINTS = {
  list: endpoints.invoices.list,
  listCSV: endpoints.invoices.listCSV,
  delete: endpoints.invoices.delete,
};

export const rowCellOptions: Record<keyof InvoiceListData, CellOptions> = {
  invoices__Id: { width: "Small" },
  invoices__FileName: { width: "SemiLarge" },
  invoices__SupplierName: { width: "SemiLarge" },
  invoices__InvoiceNumber: { width: "Medium" },
  invoices__InvoiceAmount: { width: "Medium" },
  invoices__InvoiceAmountCurrency: { width: "Small" },
  invoices__ExchangedInvoiceAmount: { width: "Medium" },
  invoices__ExchangeInvoiceAmountCurrency: { width: "Small" },
  invoices__InvoiceDate: { width: "Medium2" },
  invoices__InvoiceReceivedDate: { width: "Medium2" },
  invoices__SupplierId: { width: "Small" },
  invoices__AssignedTo: { width: "Medium" },
  invoices__bActive: { width: "Small" },
  invoices__AddDate: { width: "Medium2" },
  invoices__AddedBy: { width: "Medium2" },
  invoices__EditDate: { width: "Medium2" },
  invoices__EditedBy: { width: "SemiLarge2" },
};

/**
 * Checks endpoint priorities for role-based permissions
 * @param scopes - Array of scope objects
 * @param pathName - The path name to filter
 * @returns The highest priority permission key or null
 */
const checkEndpointPriorities = (scopes: any[], pathName: string): string | null => {
  let permissionsKey: string | null = null;

  const apClerkPermissions = scopes.filter((item: any) => item.path.endsWith("-apclerk"));
  const apCoordinatorPermissions = scopes.filter((item: any) =>
    item.path.endsWith("-apcoordinator")
  );
  const productManagerPermissions = scopes.filter((item: any) =>
    item.path.endsWith("-productmanager")
  );
  const othersPermissions = scopes.filter((item: any) => item.path.endsWith("-others"));

  if (productManagerPermissions.length === 1) {
    permissionsKey = productManagerPermissions[0].path.replace(`${pathName}/`, "");
  } else if (apCoordinatorPermissions.length === 1) {
    permissionsKey = apCoordinatorPermissions[0].path.replace(`${pathName}/`, "");
  } else if (apClerkPermissions.length === 1) {
    permissionsKey = apClerkPermissions[0].path.replace(`${pathName}/`, "");
  } else if (othersPermissions.length === 1) {
    permissionsKey = othersPermissions[0].path.replace(`${pathName}/`, "");
  } else if (scopes && scopes[0] && scopes[0].path) {
    permissionsKey = scopes[0].path.replace(`${pathName}/`, "");
  }

  return permissionsKey;
};

/**
 * Gets invoice endpoints permissions from localStorage based on user scopes
 * @param pathName - The module path name (e.g., "invoices")
 * @returns Object containing list, view, and edit permissions
 */
export const getInvoicesEndpointsPermissions = (pathName: string): EndpointsPermissions => {
  const scopes = window.localStorage.getItem("SCOPES");

  const endpointsPermissions: EndpointsPermissions = {
    list: null,
    view: null,
    edit: null,
  };

  if (scopes) {
    const parsedScopes = JSON.parse(scopes);

    if (parsedScopes && typeof parsedScopes === "object") {
      const match = parsedScopes.filter((item: any) => item.path === pathName);

      if (match.length === 1 && match[0].hasOwnProperty("childElements")) {
        const moduleScopes = match[0].childElements;

        const listScopes = moduleScopes.filter((item: any) =>
          item.path.startsWith(`${pathName}/list-`)
        );
        const viewScopes = moduleScopes.filter((item: any) =>
          item.path.startsWith(`${pathName}/view`)
        );
        const editScopes = moduleScopes.filter((item: any) =>
          item.path.startsWith(`${pathName}/update`)
        );

        const viewPermissionsKey = checkEndpointPriorities(viewScopes, pathName);
        const editPermissionsKey = checkEndpointPriorities(editScopes, pathName);

        endpointsPermissions.list = listScopes;
        endpointsPermissions.view = viewPermissionsKey;
        endpointsPermissions.edit = editPermissionsKey;
      }
    }
  }

  return endpointsPermissions;
};
